import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { observer } from "mobx-react";
import { getSnapshot } from "mobx-state-tree";
import { Form, ProgressBar } from "react-bootstrap";
import Compressor from "compressorjs";

import { Upload } from "../../Upload/Upload";
import { TimelineForm } from "./TimelineForm";

import { uploadImage, uploadVideo } from "../../../utils/upload";
import { dataURLtoFile, toastWithAsyncFetch } from "../../../utils/helper";
import { toast } from "react-toastify";
import { TOASTIFY_OPTIONS } from "../../../config/ReactToastify";

export const VideoForm = observer(
  forwardRef(({ currentLesson }, ref) => {
    const [thumbnailUrl, setThumbnailUrl] = useState(currentLesson.video?.thumbnail_url);
    const [videoUrl, setVideoUrl] = useState(currentLesson.video?.video_url);
    const [videoTime, setVideoTime] = useState(0);

    const videoRef = useRef(null);
    const thumbnailRef = useRef(null);
    const thumbnailPreviewRef = useRef(null);
    const videoPreviewRef = useRef(null);
    const timelineRef = useRef(null);

    const [isVideoUploading, setIsVideoUploading] = useState(false);
    const [uploadVideoProgress, setUploadVideoProgress] = useState(0);
    const [isThumbnailUploading, setIsThumbnailUploading] = useState(false);
    const [uploadThumbnailProgress, setUploadThumbnailProgress] = useState(0);

    useImperativeHandle(ref, () => ({
      getVideoData() {
        return {
          video_url: videoPreviewRef.current?.video_url,
          thumbnail_url: thumbnailPreviewRef.current?.url || thumbnailUrl,
        };
      },
    }));

    const uploadThumbnailHandler = (file) => {
      setThumbnailUrl(window.URL.createObjectURL(file));
      setIsThumbnailUploading(true);
      setUploadThumbnailProgress(0);

      new Compressor(file, {
        quality: 0.6,
        convertSize: 100000,
        mimeType: "auto",

        success: (result) => {
          uploadImage(result, (event) => {
            const percentComplete = Math.round((100 * event.loaded) / event.total);
            setUploadThumbnailProgress(percentComplete);
          })
            .then((res) => {
              setThumbnailUrl(res.data.link);
              thumbnailPreviewRef.current.url = res.data.link;
              toast.success("Upload ảnh đại diện thành công", TOASTIFY_OPTIONS);
            })
            .catch((e) => {
              console.log(e);
              toast.error("Upload ảnh đại diện thất bại", TOASTIFY_OPTIONS);
              setVideoUrl(currentLesson.video?.thumbnail_url);
            })
            .finally(() => {
              setIsThumbnailUploading(false);
            });
        },

        error: (err) => {
          console.log(err);
          toast.error("Nén ảnh đại diện lỗi", TOASTIFY_OPTIONS);
        },
      });
    };

    const createImagePreview = (video) => {
      const canvas = document.createElement("canvas");

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);

      return canvas.toDataURL();
    };

    const uploadVideoHandler = (file) => {
      if (!thumbnailUrl) {
        setTimeout(() => {
          const video = videoPreviewRef.current;
          const imageUrl = createImagePreview(video);
          const file = dataURLtoFile(imageUrl, "thumbnail.png");

          setThumbnailUrl(imageUrl);
          uploadThumbnailHandler(file);
        }, 500);
      }

      setVideoUrl(URL.createObjectURL(file));
      setIsVideoUploading(true);
      setUploadVideoProgress(0);

      uploadVideo(file, (event) => {
        const percentComplete = Math.round((100 * event.loaded) / (2 * event.total));
        setUploadVideoProgress(percentComplete);
      })
        .then((res) => {
          const { uri } = res.data.message;
          currentLesson.update({ video: { url: uri } });
          videoPreviewRef.current.url = uri;
          toast.success("Upload video thành công", TOASTIFY_OPTIONS);
        })
        .catch((e) => {
          console.log(e);
          toast.error("Upload video thất bại", TOASTIFY_OPTIONS);
          setVideoUrl(currentLesson.video?.video_url);
        })
        .finally(() => {
          setIsVideoUploading(false);
        });
    };

    return (
      <div className="video-form">
        <Form.Group className="d-flex flex-column mt-5">
          <Form.Label className="fw-bold">Ảnh đại diện</Form.Label>
          {thumbnailUrl && (
            <div className="position-relative">
              {isThumbnailUploading && (
                <div className="position-absolute overlay-loading">
                  <ProgressBar
                    animated
                    now={uploadThumbnailProgress}
                    style={{ height: "0.5rem" }}
                  />
                </div>
              )}

              <div
                role="button"
                className="edit-btn position-absolute bg-white text-black-50 rounded p-3 py-2"
                onClick={() => thumbnailRef.current?.inputClick()}
                style={{ display: isThumbnailUploading ? "none" : "block" }}
              >
                <i className="fa-duotone fa-pen me-2" />
                <span className="fw-semibold">Chỉnh sửa</span>
              </div>

              <img
                ref={thumbnailPreviewRef}
                src={thumbnailUrl}
                className="w-100 rounded-10"
                style={{ aspectRatio: "16 / 9" }}
              />
            </div>
          )}

          <Upload
            ref={thumbnailRef}
            accept="image/*"
            style={{ width: "100%", display: thumbnailUrl ? "none" : "flex" }}
            updateFilesCallback={(files) => uploadThumbnailHandler(files[0])}
          >
            <div className="ms-1 mb-2 text-black-25">
              <i className="fa-thin fa-image-landscape fa-3x" />
            </div>
            <span className="fs-7 text-black-25">Upload ảnh đại diện</span>
          </Upload>
        </Form.Group>

        <Form.Group className="mt-5">
          <Form.Label className="fw-bold">Video</Form.Label>
          {videoUrl && (
            <div className="position-relative">
              {isVideoUploading && (
                <div className="position-absolute overlay-loading">
                  <ProgressBar animated now={uploadVideoProgress} style={{ height: "0.5rem" }} />
                </div>
              )}

              <div
                role="button"
                className="edit-btn position-absolute bg-white text-black-50 rounded p-3 py-2"
                onClick={() => videoRef.current?.inputClick()}
                style={{ display: isVideoUploading ? "none" : "block" }}
              >
                <i className="fa-duotone fa-pen me-2" />
                <span className="fw-semibold">Chỉnh sửa</span>
              </div>

              <video
                ref={videoPreviewRef}
                controls
                className="d-block w-100 rounded-10"
                onTimeUpdate={(e) => setVideoTime(parseInt(e.target.currentTime))}
                src={videoUrl}
                style={{ aspectRatio: "16 / 9" }}
              />
            </div>
          )}
          <Upload
            ref={videoRef}
            accept="video/*"
            style={{ width: "100%", display: videoUrl ? "none" : "flex" }}
            updateFilesCallback={(files) => uploadVideoHandler(files[0])}
          >
            <div className="ms-1 mb-2 text-black-25">
              <i className="fa-thin fa-film fa-3x" />
            </div>
            <span className="fs-7 text-black-25">Upload video</span>
          </Upload>
        </Form.Group>

        <TimelineForm ref={timelineRef} currentLesson={currentLesson} videoTime={videoTime} />
      </div>
    );
  }),
);
